import { useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { type MenuItem } from '@/types/common';
import IcChevronDown from '@/assets/icons/ChevronDown.svg?react';
import NavigationLink from '../NavigationLink';
import styles from './NavGroup.module.scss';

interface NavGroupProps {
  className?: string;
  item: MenuItem;
}

/**
 * A collapsible parent item with its sub-items. Auto-opens whenever the
 * current route enters the group (so deep-linking lands you on an expanded
 * tree), but stays user-collapsible via the chevron. A user's manual collapse
 * persists while navigating between children of the same group and resets the
 * next time the user re-enters the group from outside.
 */
const NavGroup = ({ className, item }: NavGroupProps) => {
  const { t } = useTranslation(['common']);
  const parentMatch = useMatch({ path: item.href, end: false });
  const hasActiveChild = !!parentMatch;

  const [open, setOpen] = useState(hasActiveChild);
  const prevHasActiveChild = useRef(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild && !prevHasActiveChild.current) {
      setOpen(true);
    }
    prevHasActiveChild.current = hasActiveChild;
  }, [hasActiveChild]);

  const toggle = () => setOpen((value) => !value);

  return (
    <div
      className={classNames(
        styles.group,
        {
          [styles.open]: open,
          [styles.hasActive]: hasActiveChild,
        },
        className
      )}
    >
      <div className={styles.head}>
        <NavigationLink
          hasSubmenu
          highlight={item.highlight}
          href={item.href}
          Icon={item.icon}
          label={item.label}
          refreshOnSameClick={item.refreshOnSameClick}
          target={item.target}
        />
        <button
          type="button"
          className={styles.chev}
          onClick={toggle}
          aria-label={t(open ? 'common:menu.collapse' : 'common:menu.expand')}
          aria-expanded={open}
        >
          <IcChevronDown className={styles.chevIcon} />
        </button>
      </div>
      <div className={styles.children}>
        <div className={styles.childrenInner}>
          {item.submenuItems?.map((subItem) => (
            <NavigationLink
              key={subItem.label + subItem.href}
              isSub
              highlight={subItem.highlight}
              href={subItem.href}
              Icon={subItem.icon}
              label={subItem.label}
              refreshOnSameClick={subItem.refreshOnSameClick}
              target={subItem.target}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NavGroup;
